import type { IntlShape } from 'react-intl';
import { defineMessages } from 'react-intl';
import * as Yup from 'yup';

import { DESCRIPTION_CHAR_LIMIT, sumAllocatedSeats } from './plan-helpers';
import type { PatientPlanValue } from './types';

const messages = defineMessages({
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  nameDuplicate: {
    id: 'patientPlans.name.duplicate',
    defaultMessage: 'Plan names must be unique',
  },
  descriptionMax: {
    id: 'patientPlans.description.max',
    defaultMessage: 'Description must be {max} characters or fewer',
  },
  seatsInteger: {
    id: 'patientPlans.seats.integer',
    defaultMessage: 'Seats must be a whole number',
  },
  seatsMin: {
    id: 'patientPlans.seats.min',
    defaultMessage: 'Seats cannot be negative',
  },
});

interface PatientPlansSchemaOptions {
  /** The seat pool the plans are allocated from */
  totalSeats: number;
  /** Shown when the plans allocate more seats than the pool holds */
  seatPoolMessage: (allocated: number, total: number) => string;
}

/**
 * Validation for a list of patient plans, as a Yup **array** schema the
 * consumer composes into its own object schema.
 *
 * Shared because both the creation wizard and org settings enforce the same
 * `allocated <= total` rule — the wizard against the pool it just captured,
 * settings against the pool being edited — and because the uniqueness test
 * below has a non-obvious failure mode worth writing down once.
 */
export function createPatientPlansSchema(
  { formatMessage }: Pick<IntlShape, 'formatMessage'>,
  { totalSeats, seatPoolMessage }: PatientPlansSchemaOptions
) {
  return Yup.array()
    .of(
      Yup.object().shape({
        local_id: Yup.string().required(),
        name: Yup.string()
          .trim()
          .required(formatMessage(messages.requiredText))
          .test(
            'unique-plan-name',
            formatMessage(messages.nameDuplicate),
            (value, context) => {
              // Derived from this test's own path rather than taken as an
              // option: a key that could disagree with where the list is
              // actually mounted would silently pass every duplicate.
              // `plans[0].name` -> `plans`; `org.plans[0].name` -> `plans`.
              const arrayPath = context.path.slice(
                0,
                context.path.lastIndexOf('[')
              );
              const plansField = arrayPath.split('.').pop() ?? '';
              // context.from walks outward: [0] this plan, [1] the object
              // holding the plans array.
              const siblings: PatientPlanValue[] =
                context.from?.[1]?.value?.[plansField] ?? [];
              // Yup hands this test its cast values, where an untouched name is
              // absent rather than an empty string.
              const normalize = (name?: string) =>
                (name ?? '').trim().toLowerCase();
              // Flags every plan sharing the name, not just the later one.
              return (
                !value ||
                siblings.filter(
                  ({ name }) => normalize(name) === normalize(value)
                ).length <= 1
              );
            }
          ),
        description: Yup.string()
          .max(
            DESCRIPTION_CHAR_LIMIT,
            formatMessage(messages.descriptionMax, {
              max: DESCRIPTION_CHAR_LIMIT,
            })
          )
          .default(''),
        seats: Yup.number()
          // Yup casts '' to NaN, so without this a cleared field reports the
          // type error instead of `required`.
          .transform((value, original) => (original === '' ? undefined : value))
          .typeError(formatMessage(messages.seatsInteger))
          .required(formatMessage(messages.requiredText))
          .integer(formatMessage(messages.seatsInteger))
          .min(0, formatMessage(messages.seatsMin)),
        feature_ids: Yup.array().of(Yup.string().required()).default([]),
      })
    )
    .test('seat-pool', (plans = [], { createError, path }) => {
      const allocated = sumAllocatedSeats(plans as PatientPlanValue[]);
      return (
        allocated <= totalSeats ||
        createError({ path, message: seatPoolMessage(allocated, totalSeats) })
      );
    })
    .defined();
}
